import { Check, ChevronDown, ChevronUp, Globe2, Play, Users } from "lucide-react";
import { useReportPreview, useReportSubscription } from "../../hooks";
import { useAuth } from "../../hooks/useAuth";
import type { PublicReportSummary } from "../../types/reports";
import { Button, DataTable } from "../ui";
import ReportBadge from "./ReportBadge";

interface Props {
  report: PublicReportSummary;
  onSubscriptionChange: () => void | Promise<void>;
}

export default function PublicReportCard({ report, onSubscriptionChange }: Props) {
  const { user } = useAuth();
  const isOwner = report.owner.id === user?.id;
  const { preview, previewLoading, previewOpen, togglePreview } = useReportPreview(report.id);
  const {
    subscriptionLoading,
    subscriptionError,
    toggleSubscription,
  } = useReportSubscription({
    reportId: report.id,
    isSubscribed: report.is_subscribed,
    onChange: onSubscriptionChange,
  });

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-gray-800">{report.name}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Report details">
            <ReportBadge tone="blue">
              <Globe2 size={11} aria-hidden="true" /> Public
            </ReportBadge>
            {isOwner && (
              <ReportBadge>Yours</ReportBadge>
            )}
          </div>
          {report.description && <p className="mt-2 text-sm text-gray-500">{report.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
            {!isOwner && <span>By {report.owner.full_name}</span>}
            <span>Updated {new Date(report.updated_at).toLocaleDateString()}</span>
            <span className="inline-flex items-center gap-1">
              <Users size={11} aria-hidden="true" />
              {report.subscriber_count} {report.subscriber_count === 1 ? "subscriber" : "subscribers"}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {report.can_preview && (
            <Button variant="secondary" size="sm" onClick={togglePreview} disabled={previewLoading}>
              <Play size={12} aria-hidden="true" />
              {previewLoading ? "Loading..." : previewOpen ? "Hide preview" : "Preview"}
              {previewOpen ? <ChevronUp size={12} aria-hidden="true" /> : <ChevronDown size={12} aria-hidden="true" />}
            </Button>
          )}
          {!isOwner && (
            <Button
              variant={report.is_subscribed ? "secondary" : "primary"}
              size="sm"
              onClick={toggleSubscription}
              disabled={subscriptionLoading}
              aria-label={report.is_subscribed ? `Unsubscribe from ${report.name}` : `Subscribe to ${report.name}`}
              title={report.is_subscribed ? "Unsubscribe" : "Subscribe"}
            >
              {report.is_subscribed && <Check size={12} aria-hidden="true" />}
              {subscriptionLoading ? "Updating..." : report.is_subscribed ? "Subscribed" : "Subscribe"}
            </Button>
          )}
        </div>
      </div>

      {subscriptionError && <p role="alert" className="mt-3 text-sm text-red-600">{subscriptionError}</p>}
      {previewOpen && preview && (
        <div className="mt-3">
          {preview.error ? (
            <p role="alert" className="text-sm text-red-600">{preview.error}</p>
          ) : (
            <>
              <p className="mb-1 text-xs text-gray-400">{preview.row_count} rows</p>
              <DataTable rows={preview.rows} />
            </>
          )}
        </div>
      )}
    </article>
  );
}
